import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";

import { AppShell } from "@/components/layout/app-shell";
import { appConfig, getAccessToken } from "@/config";
import { AuthCallbackPage } from "@/features/auth/auth-callback-page";
import { LoginPage } from "@/features/auth/login-page";
import {
  PaymentResultPage,
  PaymentReturnPage,
} from "@/features/billing/payment-result-page";
import { PlansPage } from "@/features/billing/plans-page";
import { ChatThreadPage } from "@/features/chat/chat-thread-page";
import { ChatsPage, NoConversationPane } from "@/features/chat/chats-page";
import { ImportDeliveriesPage } from "@/features/chat/import-deliveries-page";
import { NewDeliveryPage } from "@/features/chat/new-delivery-page";
import { QuickSendPage } from "@/features/chat/quick-send-page";
import { HomePage } from "@/features/home/home-page";
import { ContactPage } from "@/features/landing/contact-page";
import { LandingPage } from "@/features/landing/landing-page";
import { PrivacyPage } from "@/features/landing/privacy-page";
import { OfferTermsPage } from "@/features/offer/offer-terms-page";
import {
  needsOnboarding,
  OnboardingPage,
} from "@/features/onboarding/onboarding-page";
import { PaymentsHistoryPage } from "@/features/settings/payments-history-page";
import { SettingsPage } from "@/features/settings/settings-page";
import { isNative } from "@/lib/capacitor";
import { getSession } from "@/session";

const rootRoute = createRootRoute({
  component: function RootComponent() {
    return <Outlet />;
  },
});

// ---------------------------------------------------------------------------
// Public routes
// ---------------------------------------------------------------------------

// "/" is the public marketing/landing page (features + download buttons).
// The native shells have no use for it — they jump straight into the app.
const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    if (isNative()) {
      throw redirect({ to: "/home", replace: true });
    }
  },
  component: LandingPage,
});

// PUBLIC landing for app-initiated payments: the phone's browser (no app
// session) arrives here after checkout and is deep-linked back into the app.
const paymentReturnRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/plans/return",
  validateSearch: (search: Record<string, unknown>) => ({
    variant:
      search.variant === "failure"
        ? ("failure" as const)
        : ("success" as const),
    paymentId:
      typeof search.paymentId === "string" ? search.paymentId : undefined,
  }),
  component: function PaymentReturnRoute() {
    const { variant, paymentId } = paymentReturnRoute.useSearch();
    return <PaymentReturnPage variant={variant} paymentId={paymentId} />;
  },
});

// Public privacy policy — the store listings require a reachable URL.
const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy",
  component: PrivacyPage,
});

// Free-messages offer terms — public, reached only from the banner's info icon.
const offerTermsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/offer-terms",
  component: OfferTermsPage,
});

// Public contact page (support WhatsApp + email).
const contactRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/contact",
  component: ContactPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  beforeLoad: () => {
    if (getAccessToken()) {
      throw redirect({ to: "/home" });
    }
  },
  component: LoginPage,
});

const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/callback",
  validateSearch: (search: Record<string, unknown>) => ({
    sessionId:
      typeof search.sessionId === "string" ? search.sessionId : undefined,
  }),
  component: function AuthCallbackRoute() {
    const { sessionId } = authCallbackRoute.useSearch();
    return <AuthCallbackPage sessionId={sessionId} />;
  },
});

// Onboarding is authenticated but rendered outside the AppShell (no bottom
// nav) — a sibling of the app layout, like /login.
const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  beforeLoad: () => {
    if (!getAccessToken()) {
      throw redirect({ to: "/login" });
    }
  },
  component: OnboardingPage,
});

// ---------------------------------------------------------------------------
// Authenticated layout
// ---------------------------------------------------------------------------

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: () => {
    if (!getAccessToken()) {
      throw redirect({ to: "/login" });
    }
  },
  component: AppShell,
});

const homeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/home",
  beforeLoad: () => {
    // First run: send drivers with a bare profile through onboarding once.
    const session = getSession();
    if (session && needsOnboarding(session.driver)) {
      throw redirect({ to: "/onboarding" });
    }
  },
  component: HomePage,
});

const chatsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/chats",
  // ?archived=true switches the list into the archive view — a URL so the
  // browser/hardware back gesture leaves the archive naturally.
  validateSearch: (search: Record<string, unknown>): { archived?: true } => ({
    archived:
      search.archived === true || search.archived === "true" ? true : undefined,
  }),
  component: function ChatsRoute() {
    // Desktop shows the conversations list as a persistent aside (AppShell),
    // so the route's own pane is the list on phones and a "pick a chat"
    // placeholder beside the aside from md up.
    return (
      <>
        <div className="tw:h-full tw:md:hidden">
          <ChatsPage />
        </div>
        <NoConversationPane />
      </>
    );
  },
});

const newDeliveryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/chats/new",
  // ?phone=9665... prefills the client number — the WhatsApp-link handler
  // (open a wa.me link with the app) lands here.
  validateSearch: (search: Record<string, unknown>): { phone?: string } =>
    typeof search.phone === "string" && /^\d{5,15}$/.test(search.phone)
      ? { phone: search.phone }
      : {},
  component: function NewDeliveryRoute() {
    const { phone } = newDeliveryRoute.useSearch();
    return <NewDeliveryPage initialPhone={phone} />;
  },
});

// Quick send — where WhatsApp links opened WITH the app land: pick a
// template, fill only what it needs, send. ?phone= prefills the number.
const quickSendRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/chats/quick",
  validateSearch: (search: Record<string, unknown>): { phone?: string } =>
    typeof search.phone === "string" && /^\d{5,15}$/.test(search.phone)
      ? { phone: search.phone }
      : {},
  component: function QuickSendRoute() {
    const { phone } = quickSendRoute.useSearch();
    return <QuickSendPage initialPhone={phone} />;
  },
});

const importDeliveriesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/chats/import",
  // ?source=contacts jumps straight into the pick-from-contacts flow.
  validateSearch: (search: Record<string, unknown>): { source?: "contacts" } =>
    search.source === "contacts" ? { source: "contacts" } : {},
  component: function ImportDeliveriesRoute() {
    const { source } = importDeliveriesRoute.useSearch();
    return <ImportDeliveriesPage initialSource={source} />;
  },
});

const chatThreadRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/chats/$conversationId",
  component: function ChatThreadRoute() {
    const { conversationId } = chatThreadRoute.useParams();
    return <ChatThreadPage conversationId={conversationId} />;
  },
});

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings",
  component: SettingsPage,
});

const paymentsHistoryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/payments",
  component: PaymentsHistoryPage,
});

const plansRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/plans",
  component: PlansPage,
});

const plansSuccessRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/plans/success",
  validateSearch: (search: Record<string, unknown>) => ({
    paymentId:
      typeof search.paymentId === "string" ? search.paymentId : undefined,
    // "app": checkout started in the native shell, paid in the browser — the
    // browser-side page deep-links back into the app.
    from: search.from === "app" ? ("app" as const) : undefined,
  }),
  component: function PlansSuccessRoute() {
    const { paymentId, from } = plansSuccessRoute.useSearch();
    return (
      <PaymentResultPage
        variant="success"
        paymentId={paymentId}
        fromApp={from === "app"}
      />
    );
  },
});

const plansFailureRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/plans/failure",
  validateSearch: (search: Record<string, unknown>) => ({
    from: search.from === "app" ? ("app" as const) : undefined,
  }),
  component: function PlansFailureRoute() {
    const { from } = plansFailureRoute.useSearch();
    return <PaymentResultPage variant="failure" fromApp={from === "app"} />;
  },
});

const routeTree = rootRoute.addChildren([
  landingRoute,
  paymentReturnRoute,
  privacyRoute,
  offerTermsRoute,
  contactRoute,
  loginRoute,
  authCallbackRoute,
  onboardingRoute,
  appRoute.addChildren([
    homeRoute,
    chatsRoute,
    newDeliveryRoute,
    quickSendRoute,
    importDeliveriesRoute,
    chatThreadRoute,
    settingsRoute,
    paymentsHistoryRoute,
    plansRoute,
    plansSuccessRoute,
    plansFailureRoute,
  ]),
]);

export function createAppRouter() {
  return createRouter({
    routeTree,
    basepath: appConfig.routerBasePath,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
